import axiosInstance from '../../api/api';
import {
  clearPromptVideoUpload,
  newUploadClientId,
  trackPromptVideoUpload,
  type PromptVideoUploadPhase,
} from './promptVideoUploadTracker';

export type PromptVideoUploadProgress = {
  phase: PromptVideoUploadPhase;
  /** 0–100 overall progress for presign + S3 + optional save hook */
  percent: number;
};

type PresignResponse = {
  uploadUrl: string;
  publicUrl: string;
  requiredHeaders?: Record<string, string>;
};

function normalizeContentType(file: File): string {
  const type = (file.type || '').toLowerCase().split(';')[0].trim();
  if (type.startsWith('video/')) return type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.webm')) return 'video/webm';
  if (name.endsWith('.mov')) return 'video/quicktime';
  if (name.endsWith('.mp4')) return 'video/mp4';
  return 'video/mp4';
}

export function putFileWithProgress(
  uploadUrl: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (loaded: number, total: number) => void
): { promise: Promise<void>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new Error(
          xhr.status === 0
            ? 'Upload blocked — check S3 CORS for this bucket.'
            : `Video upload failed (${xhr.status}).`
        )
      );
    });
    xhr.addEventListener('error', () => {
      reject(new Error('Network error while uploading video.'));
    });
    xhr.addEventListener('abort', () => {
      reject(new Error('Video upload was cancelled.'));
    });
    xhr.open('PUT', uploadUrl);
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.send(file);
  });

  return {
    promise,
    abort: () => xhr.abort(),
  };
}

export async function uploadPromptVideoDirect(
  file: File,
  options: {
    clientId?: string;
    promptId?: number | null;
    onProgress?: (progress: PromptVideoUploadProgress) => void;
    registerAbort?: (abort: () => void) => void;
  } = {}
): Promise<string> {
  const clientId = options.clientId ?? newUploadClientId();
  const emit = (phase: PromptVideoUploadPhase, percent: number) => {
    trackPromptVideoUpload(clientId, {
      promptId: options.promptId ?? null,
      phase,
      progress: percent,
    });
    options.onProgress?.({ phase, percent });
  };

  emit('presigning', 2);

  const contentType = normalizeContentType(file);
  const presignController = new AbortController();
  options.registerAbort?.(() => presignController.abort());

  const { data } = await axiosInstance.post<PresignResponse>(
    'prompt/video/presign',
    {
      content_type: contentType,
      filename: file.name,
    },
    { signal: presignController.signal }
  );

  if (!data?.uploadUrl || !data?.publicUrl) {
    trackPromptVideoUpload(clientId, {
      phase: 'error',
      progress: 0,
      error: 'Could not start video upload.',
    });
    throw new Error('Could not start video upload.');
  }

  const headers = {
    ...(data.requiredHeaders ?? {}),
    'Content-Type': contentType,
  };

  emit('uploading', 5);

  try {
    const upload = putFileWithProgress(data.uploadUrl, file, headers, (loaded, total) => {
      const ratio = total > 0 ? loaded / total : 0;
      const percent = Math.min(92, Math.round(5 + ratio * 87));
      emit('uploading', percent);
    });
    options.registerAbort?.(() => upload.abort());
    await upload.promise;

    emit('uploading', 95);
    // Onboarding / create-prompt uploads have no promptId — do not leave session
    // entries that look like an interrupted /prompts upload after navigation.
    if (options.promptId == null) {
      clearPromptVideoUpload(clientId);
    }
    return data.publicUrl;
  } catch (err) {
    trackPromptVideoUpload(clientId, {
      promptId: options.promptId ?? null,
      phase: 'error',
      progress: 0,
      error: err instanceof Error ? err.message : 'Video upload failed.',
    });
    if (options.promptId == null) {
      clearPromptVideoUpload(clientId);
    }
    throw err;
  }
}

export async function uploadPromptVideoDirectWithSavePhase(
  file: File,
  options: {
    clientId?: string;
    promptId?: number | null;
    onProgress?: (progress: PromptVideoUploadProgress) => void;
  },
  saveFn: (publicUrl: string) => Promise<void>
): Promise<string> {
  const clientId = options.clientId ?? newUploadClientId();
  const publicUrl = await uploadPromptVideoDirect(file, {
    ...options,
    clientId,
    onProgress: (progress) => {
      if (progress.phase === 'uploading' && progress.percent >= 95) {
        options.onProgress?.({ phase: 'saving', percent: 96 });
        trackPromptVideoUpload(clientId, {
          promptId: options.promptId ?? null,
          phase: 'saving',
          progress: 96,
        });
        return;
      }
      options.onProgress?.(progress);
    },
  });

  trackPromptVideoUpload(clientId, {
    promptId: options.promptId ?? null,
    phase: 'saving',
    progress: 97,
  });
  options.onProgress?.({ phase: 'saving', percent: 97 });

  await saveFn(publicUrl);

  trackPromptVideoUpload(clientId, {
    promptId: options.promptId ?? null,
    phase: 'done',
    progress: 100,
  });
  options.onProgress?.({ phase: 'done', percent: 100 });
  clearPromptVideoUpload(clientId);

  return publicUrl;
}
